import React from 'react';
import { ArrowRight, ArrowLeft, Home } from 'lucide-react';

const NotFoundPage = () => {
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col overflow-hidden relative">
            {/* Background Decoration */}
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-neutral-200 rounded-full opacity-30 blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-green-100 rounded-full opacity-30 blur-3xl"></div>

            {/* Header */}
            <header className="py-6 px-6 md:px-12">
                <div className="flex items-center">
                    <div className="font-bold text-2xl">
                        <span className="text-green-600">Urban</span><span className="text-gray-900">Shine</span>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-grow flex flex-col items-center justify-center px-6 py-12">
                <div className="relative">
                    {/* 404 Background Text */}
                    <div className="absolute -z-10 text-gray-100 text-[12rem] md:text-[20rem] font-bold opacity-60 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 select-none">
                        404
                    </div>

                    {/* Content Card */}
                    <div className="bg-white rounded-xl shadow-xl p-8 md:p-12 w-full max-w-lg text-center z-10 relative">
                        {/* Glitch Effect Decoration */}
                        <div className="absolute -top-12 -right-12 w-24 h-24 bg-neutral-400 opacity-20 rounded-full transform rotate-45"></div>
                        <div className="absolute -bottom-12 -left-12 w-24 h-24 bg-green-500 opacity-20 rounded-full transform rotate-45"></div>

                        {/* Icon */}
                        <div className="mb-6 inline-flex">
                            <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>

                        {/* Text Content */}
                        <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800">
                            Page Not Found
                        </h1>

                        <p className="text-gray-600 mb-8 max-w-md mx-auto">
                            Oops! It seems like this page has been lost in the laundry.
                            Let's help you find your way back to clean clothes and fresh service.
                        </p>

                        {/* Buttons */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={() => window.history.back()}
                                className="border border-green-700 text-green-700 hover:bg-green-50 font-medium py-3 px-6 rounded-full flex items-center transition-all duration-300 w-full sm:w-auto justify-center"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Go Back
                            </button>

                            <a
                                href="/"
                                className="bg-neutral-500 text-white font-medium py-3 px-6 rounded-full hover:bg-green-700 transition-all duration-300 w-full sm:w-auto text-center flex items-center justify-center"
                            >
                                Return Home
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Additional Help */}
                {/* <div className="mt-12 text-center">
                    <h2 className="text-lg font-medium text-gray-700 mb-4">
                        Looking for our laundry services?
                    </h2>
                    <div className="flex flex-wrap justify-center gap-6">
                        <a href="/services" className="text-green-600 hover:text-neutral-500 transition-colors font-medium flex items-center">
                            Our Services
                        </a>
                        <a href="/contact" className="text-green-600 hover:text-neutral-500 transition-colors font-medium flex items-center">
                            Contact Us
                        </a>
                        <a href="/about" className="text-green-600 hover:text-neutral-500 transition-colors font-medium flex items-center">
                            About Us
                        </a>
                    </div>
                </div> */}
            </main>

            {/* Animation */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(5)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute bg-neutral-300 opacity-20 rounded-full animate-float-slow"
                        style={{
                            width: `${Math.random() * 10 + 5}px`,
                            height: `${Math.random() * 10 + 5}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 5}s`,
                            animationDuration: `${Math.random() * 10 + 15}s`,
                        }}
                    ></div>
                ))}
                {[...Array(5)].map((_, i) => (
                    <div
                        key={i + 5}
                        className="absolute bg-green-200 opacity-20 rounded-full animate-float-slow"
                        style={{
                            width: `${Math.random() * 10 + 5}px`,
                            height: `${Math.random() * 10 + 5}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 5}s`,
                            animationDuration: `${Math.random() * 10 + 15}s`,
                        }}
                    ></div>
                ))}
            </div>

            {/* Laundry-themed bubble animations */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(8)].map((_, i) => (
                    <div
                        key={i + 10}
                        className="absolute rounded-full animate-rise"
                        style={{
                            width: `${Math.random() * 20 + 10}px`,
                            height: `${Math.random() * 20 + 10}px`,
                            bottom: `-${Math.random() * 20 + 10}px`,
                            left: `${Math.random() * 100}%`,
                            background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.4))',
                            boxShadow: 'inset 0 0 10px rgba(255, 255, 255, 0.3)',
                            opacity: 0.6,
                            animationDelay: `${Math.random() * 10}s`,
                            animationDuration: `${Math.random() * 10 + 15}s`,
                        }}
                    ></div>
                ))}
            </div>

            {/* Footer */}
            <footer className="py-6 px-6 md:px-12 text-center text-gray-600 text-sm">
                <p>© {new Date().getFullYear()} <span className="text-green-600">Urban</span><span className="text-gray-900">Shine</span> Laundry. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default NotFoundPage;